import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, LogOut, Plus, Trash2, Sparkles, Copy, X, ExternalLink, Phone, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const STATUTS = ['À contacter', 'Premier contact effectué', 'Relance à effectuer', 'Réponse reçue', 'Rendez-vous prévu', 'Devis demandé', 'Client signé', 'Sans suite'];
const CATEGORIES = ['Wedding planner', 'Domaine / Lieu de réception', 'Traiteur', 'Agence événementielle', 'Entreprise', 'Hôtel', 'Salon du mariage', 'Prestataire mariage', 'Autre'];
const MSG_TYPES = [
  { key: 'email', label: 'Email 1er contact' },
  { key: 'linkedin', label: 'Message LinkedIn' },
  { key: 'relance', label: 'Relance' },
  { key: 'partenariat', label: 'Partenariat' },
  { key: 'pitch', label: 'Pitch téléphone 30s' },
];
const PRIO_COLORS = { A: 'bg-emerald_ink text-white', B: 'bg-gold text-white', C: 'bg-char/20 text-char' };

const getToken = () => localStorage.getItem('ofv_token');
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Une erreur est survenue.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
  return String(detail);
}

function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
      localStorage.setItem('ofv_token', data.access_token);
      onLogin();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6" data-testid="prospection-login">
      <form onSubmit={submit} className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-8">
          <span className="w-10 h-10 border border-gold/50 text-gold flex items-center justify-center"><Lock size={16} /></span>
          <div>
            <div className="font-serif text-2xl">Espace privé</div>
            <div className="overline mt-0.5">Prospection — Ô Fil des Verres</div>
          </div>
        </div>
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="field w-full mb-4" data-testid="login-email-input" />
        <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mot de passe" className="field w-full mb-6" data-testid="login-password-input" />
        {error && <div className="text-red-700 text-sm mb-4" data-testid="login-error">{error}</div>}
        <button type="submit" disabled={loading} className="w-full bg-gold text-cream py-3.5 text-[12px] tracking-[0.2em] uppercase hover:bg-char transition-colors disabled:opacity-60" data-testid="login-submit-button">
          {loading ? 'Connexion…' : 'Se connecter'}
        </button>
      </form>
    </div>
  );
}

const EMPTY = { entreprise: '', categorie: 'Wedding planner', ville: '', site: '', contact_nom: '', email: '', telephone: '', linkedin: '', potentiel: '', note: 70, priorite: 'B', statut: 'À contacter', prochaine_action: '', date_relance: '', source: '', notes_libres: '' };

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Drawer({ prospect, onClose, onSaved, onDeleted, onMessageAdded }) {
  const isNew = !prospect.id;
  const [form, setForm] = useState({ ...EMPTY, ...prospect });
  const [genType, setGenType] = useState('email');
  const [genText, setGenText] = useState('');
  const [generating, setGenerating] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const save = async () => {
    try {
      const payload = { ...form, note: Number(form.note) || 0 };
      let data;
      if (isNew) {
        ({ data } = await axios.post(`${API}/api/prospects`, payload, { headers: authHeaders() }));
      } else {
        ({ data } = await axios.put(`${API}/api/prospects/${form.id}`, payload, { headers: authHeaders() }));
      }
      toast.success('Prospect enregistré');
      onSaved(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Supprimer ${form.entreprise} ?`)) return;
    try {
      await axios.delete(`${API}/api/prospects/${form.id}`, { headers: authHeaders() });
      toast.success('Prospect supprimé');
      onDeleted(form.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const generate = async () => {
    setGenerating(true);
    setGenText('');
    try {
      const res = await fetch(`${API}/api/prospects/${form.id}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ type: genType }),
      });
      if (!res.ok) throw new Error('Erreur de génération');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setGenText(acc);
      }
      if (acc.trim()) {
        const newMsg = { type: genType, text: acc, created_at: new Date().toISOString() };
        setForm((f) => ({ ...f, messages: [...(f.messages || []), newMsg] }));
        onMessageAdded(form.id, newMsg);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papier');
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="prospect-drawer">
      <div className="flex-1 bg-char/40" onClick={onClose} />
      <div className="w-full max-w-2xl bg-cream h-full overflow-y-auto p-6 md:p-10 border-l border-char/10">
        <div className="flex items-center justify-between mb-8">
          <div className="font-serif text-2xl">{isNew ? 'Nouveau prospect' : form.entreprise}</div>
          <button onClick={onClose} className="p-2 text-char/60 hover:text-char" data-testid="drawer-close-button"><X size={20} /></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Entreprise *"><input className="field w-full" value={form.entreprise} onChange={set('entreprise')} data-testid="form-entreprise" /></Field>
          <Field label="Catégorie">
            <select className="field w-full" value={form.categorie} onChange={set('categorie')} data-testid="form-categorie">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Ville"><input className="field w-full" value={form.ville} onChange={set('ville')} /></Field>
          <Field label="Site internet"><input className="field w-full" value={form.site} onChange={set('site')} /></Field>
          <Field label="Contact (décideur)"><input className="field w-full" value={form.contact_nom} onChange={set('contact_nom')} /></Field>
          <Field label="Email pro"><input className="field w-full" value={form.email} onChange={set('email')} /></Field>
          <Field label="Téléphone"><input className="field w-full" value={form.telephone} onChange={set('telephone')} /></Field>
          <Field label="LinkedIn"><input className="field w-full" value={form.linkedin} onChange={set('linkedin')} /></Field>
          <Field label="Note /100"><input type="number" min="0" max="100" className="field w-full" value={form.note} onChange={set('note')} data-testid="form-note" /></Field>
          <Field label="Priorité">
            <select className="field w-full" value={form.priorite} onChange={set('priorite')} data-testid="form-priorite">
              <option value="A">A — contacter rapidement</option>
              <option value="B">B — potentiel intéressant</option>
              <option value="C">C — prospection ultérieure</option>
            </select>
          </Field>
          <Field label="Statut">
            <select className="field w-full" value={form.statut} onChange={set('statut')} data-testid="form-statut">
              {STATUTS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Date de relance"><input type="date" className="field w-full" value={form.date_relance} onChange={set('date_relance')} /></Field>
        </div>
        <div className="mt-5 space-y-5">
          <Field label="Potentiel"><input className="field w-full" value={form.potentiel} onChange={set('potentiel')} /></Field>
          <Field label="Prochaine action"><input className="field w-full" value={form.prochaine_action} onChange={set('prochaine_action')} /></Field>
          <Field label="Source (lien vérifiable)"><input className="field w-full" value={form.source} onChange={set('source')} /></Field>
          <Field label="Notes"><textarea rows={3} className="field w-full" value={form.notes_libres} onChange={set('notes_libres')} /></Field>
        </div>

        <div className="flex gap-3 mt-8">
          <button onClick={save} className="bg-gold text-cream px-8 py-3 text-[12px] tracking-[0.2em] uppercase hover:bg-char transition-colors" data-testid="form-save-button">Enregistrer</button>
          {!isNew && (
            <button onClick={remove} className="border border-red-800/40 text-red-800 px-4 py-3 hover:bg-red-800 hover:text-white transition-colors" data-testid="form-delete-button"><Trash2 size={16} /></button>
          )}
        </div>

        {!isNew && (
          <div className="mt-12 border-t border-char/10 pt-8">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={16} className="text-gold" />
              <span className="overline">Générer un message IA</span>
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              {MSG_TYPES.map((t) => (
                <button key={t.key} onClick={() => setGenType(t.key)}
                  className={`px-4 py-2 text-xs border transition-colors ${genType === t.key ? 'bg-char text-cream border-char' : 'border-char/25 text-char/70 hover:border-gold'}`}
                  data-testid={`msgtype-${t.key}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <button onClick={generate} disabled={generating} className="bg-emerald_ink text-white px-6 py-3 text-[12px] tracking-[0.15em] uppercase hover:opacity-85 transition-opacity disabled:opacity-50" data-testid="generate-message-button">
              {generating ? 'Génération en cours…' : 'Générer le message'}
            </button>
            {genText && (
              <div className="mt-5 relative bg-white/60 border border-char/10 p-5 text-sm whitespace-pre-wrap leading-relaxed" data-testid="generated-message">
                <button onClick={() => copy(genText)} className="absolute top-3 right-3 text-char/50 hover:text-gold" data-testid="copy-message-button"><Copy size={15} /></button>
                {genText}
              </div>
            )}
            {(form.messages || []).length > 0 && (
              <div className="mt-8">
                <div className="overline mb-3">Messages précédents ({form.messages.length})</div>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {[...form.messages].reverse().map((m, i) => (
                    <details key={i} className="bg-white/40 border border-char/10 p-3 text-sm">
                      <summary className="cursor-pointer text-char/70">{MSG_TYPES.find((t) => t.key === m.type)?.label || m.type} — {new Date(m.created_at).toLocaleDateString('fr-FR')}</summary>
                      <div className="mt-2 whitespace-pre-wrap relative pr-8">
                        <button onClick={() => copy(m.text)} className="absolute top-0 right-0 text-char/50 hover:text-gold"><Copy size={14} /></button>
                        {m.text}
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Prospection() {
  const [authed, setAuthed] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [fPrio, setFPrio] = useState('');
  const [fStatut, setFStatut] = useState('');
  const [fCat, setFCat] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/prospects`, { headers: authHeaders() });
      setProspects(data);
      setAuthed(true);
    } catch {
      localStorage.removeItem('ofv_token');
      setAuthed(false);
    }
  }, []);

  useEffect(() => {
    if (getToken()) load();
    else setAuthed(false);
  }, [load]);

  if (authed === null) return <div className="min-h-screen bg-cream flex items-center justify-center text-char/50">Chargement…</div>;
  if (authed === false) return <Login onLogin={load} />;

  const filtered = prospects.filter((p) =>
    (!fPrio || p.priorite === fPrio) &&
    (!fStatut || p.statut === fStatut) &&
    (!fCat || p.categorie === fCat) &&
    (!search || `${p.entreprise} ${p.ville} ${p.contact_nom}`.toLowerCase().includes(search.toLowerCase()))
  );

  const stats = {
    total: prospects.length,
    a: prospects.filter((p) => p.priorite === 'A').length,
    acontacter: prospects.filter((p) => p.statut === 'À contacter').length,
    encours: prospects.filter((p) => !['À contacter', 'Client signé', 'Sans suite'].includes(p.statut)).length,
    signes: prospects.filter((p) => p.statut === 'Client signé').length,
  };

  const updateStatut = async (p, statut) => {
    try {
      const { data } = await axios.put(`${API}/api/prospects/${p.id}`, { statut }, { headers: authHeaders() });
      setProspects((list) => list.map((x) => (x.id === p.id ? data : x)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-screen bg-cream text-char" data-testid="prospection-dashboard">
      <header className="border-b border-char/10 px-6 md:px-10 py-5 flex items-center justify-between sticky top-0 bg-cream/90 backdrop-blur-lg z-40">
        <div>
          <div className="font-serif text-xl">Prospection commerciale</div>
          <div className="overline mt-0.5">Ô Fil des Verres — espace privé</div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setSelected({})} className="inline-flex items-center gap-2 bg-gold text-cream px-5 py-2.5 text-[11px] tracking-[0.15em] uppercase hover:bg-char transition-colors" data-testid="add-prospect-button">
            <Plus size={14} /> Prospect
          </button>
          <button onClick={() => { localStorage.removeItem('ofv_token'); setAuthed(false); }} className="p-2.5 border border-char/15 text-char/60 hover:text-char hover:border-char/40 transition-colors" data-testid="logout-button" aria-label="Déconnexion">
            <LogOut size={15} />
          </button>
        </div>
      </header>

      <div className="px-6 md:px-10 py-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-char/10 border border-char/10 mb-8">
          {[['Prospects', stats.total], ['Priorité A', stats.a], ['À contacter', stats.acontacter], ['En cours', stats.encours], ['Signés', stats.signes]].map(([l, v]) => (
            <div key={l} className="bg-cream p-4">
              <div className="font-serif text-3xl text-gold">{v}</div>
              <div className="overline mt-1">{l}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-3 mb-6 items-end">
          <input placeholder="Rechercher…" value={search} onChange={(e) => setSearch(e.target.value)} className="field field-inline w-48" data-testid="search-input" />
          <select value={fPrio} onChange={(e) => setFPrio(e.target.value)} className="field field-inline" data-testid="filter-priorite">
            <option value="">Toutes priorités</option>
            <option value="A">Priorité A</option><option value="B">Priorité B</option><option value="C">Priorité C</option>
          </select>
          <select value={fStatut} onChange={(e) => setFStatut(e.target.value)} className="field field-inline" data-testid="filter-statut">
            <option value="">Tous statuts</option>
            {STATUTS.map((s) => <option key={s}>{s}</option>)}
          </select>
          <select value={fCat} onChange={(e) => setFCat(e.target.value)} className="field field-inline" data-testid="filter-categorie">
            <option value="">Toutes catégories</option>
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <span className="text-char/50 text-sm pb-3">{filtered.length} résultat(s)</span>
        </div>

        <div className="overflow-x-auto border border-char/10">
          <table className="w-full text-sm" data-testid="prospects-table">
            <thead>
              <tr className="border-b border-char/10 bg-white/40">
                {['Entreprise', 'Catégorie', 'Ville', 'Contact', 'Note', 'Prio', 'Statut', 'Prochaine action', ''].map((h) => (
                  <th key={h} className="text-left px-4 py-3 overline whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-char/5 hover:bg-white/50 cursor-pointer" onClick={() => setSelected(p)} data-testid={`prospect-row-${p.id}`}>
                  <td className="px-4 py-3 font-medium whitespace-nowrap">
                    {p.entreprise}
                    <div className="flex gap-2 mt-1 text-char/40">
                      {p.site && <a href={p.site} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="hover:text-gold"><ExternalLink size={12} /></a>}
                      {p.telephone && <a href={`tel:${p.telephone}`} onClick={(e) => e.stopPropagation()} className="hover:text-gold"><Phone size={12} /></a>}
                      {p.email && <a href={`mailto:${p.email}`} onClick={(e) => e.stopPropagation()} className="hover:text-gold"><Mail size={12} /></a>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-char/70 whitespace-nowrap">{p.categorie}</td>
                  <td className="px-4 py-3 text-char/70 whitespace-nowrap">{p.ville}</td>
                  <td className="px-4 py-3 text-char/70 whitespace-nowrap">{p.contact_nom || '—'}</td>
                  <td className="px-4 py-3 font-mono">{p.note}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 text-xs ${PRIO_COLORS[p.priorite] || ''}`}>{p.priorite}</span></td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <select value={p.statut} onChange={(e) => updateStatut(p, e.target.value)} className="bg-transparent border border-char/15 px-2 py-1 text-xs" data-testid={`statut-select-${p.id}`}>
                      {STATUTS.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-char/60 max-w-[280px] truncate">{p.prochaine_action}</td>
                  <td className="px-4 py-3 text-gold text-xs whitespace-nowrap">Ouvrir →</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-char/50">Aucun prospect ne correspond aux filtres.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <Drawer
          prospect={selected}
          onClose={() => setSelected(null)}
          onSaved={(p) => { setProspects((list) => { const i = list.findIndex((x) => x.id === p.id); const next = i === -1 ? [p, ...list] : list.map((x) => (x.id === p.id ? p : x)); return next.sort((a, b) => b.note - a.note); }); setSelected(null); }}
          onDeleted={(id) => { setProspects((list) => list.filter((x) => x.id !== id)); setSelected(null); }}
          onMessageAdded={(id, msg) => setProspects((list) => list.map((x) => (x.id === id ? { ...x, messages: [...(x.messages || []), msg] } : x)))}
        />
      )}
    </div>
  );
}
